import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Divider } from 'primereact/divider';
import { StyleClass } from 'primereact/styleclass';
import { useCart } from '../../contexts/CartContext';
import { useCartActions } from '../../hooks/useCartActions';
import { useNavigate } from 'react-router-dom';
import ProductImage from './ProductImage';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from './AuthModal';

export default function SlideoverCart({ isOpen, onClose }) {
    const btnRef1 = useRef(null);
    const btnRef2 = useRef(null);
    const btnRef3 = useRef(null);
    const navigate = useNavigate();
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [authModalVisible, setAuthModalVisible] = useState(false);
    
    const { items, total, itemCount } = useCart();
    const { updateCartQuantity, removeFromCart, formatPrice } = useCartActions();

    const handleCheckout = () => {
        if (!isAuthenticated && !authLoading) {
            setAuthModalVisible(true);
            return;
        }
        onClose();
        navigate('/checkout');
    };

    const handleAuthSuccess = () => {
        setAuthModalVisible(false);
        onClose();
        setTimeout(() => navigate('/checkout'), 250);
    };

    const handleContinueShopping = () => {
        onClose();
    };

    return (
        <>
            {/* Overlay de fondo */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={onClose}
                    style={{ zIndex: 1040 }}
                />
            )}
            
            {/* Panel deslizante */}
            <div 
                id="slideover-cart" 
                className={`${isOpen ? 'block' : 'hidden'} surface-overlay fixed top-0 right-0 shadow-2 w-full md:w-30rem h-full z-50`}
                style={{ zIndex: 1050 }}
            >
                <div className="flex flex-column h-full">
                    {/* Header */}
                    <div className="surface-100 p-3 flex align-items-center">
                        <Button 
                            ref={btnRef2} 
                            icon="pi pi-times" 
                            className="p-button-text p-button-rounded" 
                            onClick={onClose}
                        />
                        <div className="border-right-1 border-300 mx-3"></div>
                        <span className="text-900 text-xl font-medium inline-flex align-items-center ml-1">
                            Tu Carrito ({itemCount})
                        </span>
                    </div>

                    {/* Contenido del carrito */}
                    <div className="flex-auto overflow-y-auto py-5 px-3 md:px-5">
                        {items.length === 0 ? (
                            <div className="text-center py-8">
                                <i className="pi pi-shopping-cart text-6xl text-color-secondary mb-4"></i>
                                <h3 className="text-xl font-semibold mb-4">Tu carrito está vacío</h3>
                                <p className="text-color-secondary mb-6">
                                    Explora nuestro catálogo y encuentra los productos que necesitas
                                </p>
                                <Button 
                                    label="Continuar Comprando"
                                    icon="pi pi-shopping-bag"
                                    className="p-button-primary"
                                    onClick={handleContinueShopping}
                                />
                            </div>
                        ) : (
                            <>
                                {/* Items del carrito */}
                                {items.map((item, index) => (
                                    <div key={item.id} className="flex align-items-start sm:align-items-center mb-2 border-bottom-1 surface-border">
                                        <ProductImage 
                                            src={item.imagen} 
                                            alt={item.nombre}
                                            className="w-6rem sm:w-8rem flex-shrink-0"
                                            style={{ maxWidth: '100%' }}
                                        />
                                        <div className="flex-auto px-3">
                                            <div className="flex align-items-center justify-content-between mb-3">
                                                <span className="text-900 font-medium text-sm line-height-3">
                                                    {item.nombre}
                                                </span>
                                                <span className="text-900 font-bold text-right">
                                                    {formatPrice(item.precio_venta)}
                                                </span>
                                            </div>
                                            <div className="text-600 text-sm mb-3">
                                                Subtotal: {formatPrice(item.precio_venta * item.cantidad)}
                                            </div>
                                            <div className="flex flex-auto justify-content-between align-items-center">
                                                <InputNumber 
                                                    showButtons 
                                                    buttonLayout="horizontal" 
                                                    min={1} 
                                                    max={item.stock_disponible || 999}
                                                    inputClassName="w-3rem text-center py-2 px-1 border-transparent" 
                                                    value={item.cantidad}
                                                    onValueChange={(e) => updateCartQuantity(item.id, e.value, item.stock_disponible, item.nombre)}
                                                    className="border-1 surface-border border-round" 
                                                    decrementButtonClassName="p-button-text py-1 px-1" 
                                                    incrementButtonClassName="p-button-text py-1 px-1" 
                                                    incrementButtonIcon="pi pi-plus" 
                                                    decrementButtonIcon="pi pi-minus" 
                                                />
                                                <Button 
                                                    icon="pi pi-trash" 
                                                    className="p-button-danger p-button-text p-button-rounded" 
                                                    onClick={() => removeFromCart(item.id, item.nombre)}
                                                    tooltip="Eliminar producto"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Mensaje de confirmación si hay items */}
                                {items.length > 0 && (
                                    <div className="flex align-items-center bg-green-50 text-green-600 text-sm p-2 border-round mb-4">
                                        <i className="pi pi-check mr-2"></i>
                                        <span>Productos agregados a tu carrito.</span>
                                    </div>
                                )}

                                <Divider className="text-sm text-500">Resumen</Divider>
                            </>
                        )}
                    </div>

                    {/* Footer con total y botones */}
                    {items.length > 0 && (
                        <div className="border-top-1 surface-border p-3">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <span className="text-900 font-medium">
                                    Total <span className="text-600 text-sm">inc. IGV</span>
                                </span>
                                <span className="text-900 font-bold text-xl">
                                    {formatPrice(total)}
                                </span>
                            </div>
                            <Button 
                                className="p-button-success mb-3 w-full" 
                                label="Proceder al Checkout" 
                                icon="pi pi-credit-card"
                                onClick={handleCheckout}
                            />
                            <Button 
                                ref={btnRef3} 
                                className="p-button-outlined p-button-secondary w-full" 
                                label="Continuar Comprando" 
                                icon="pi pi-shopping-bag"
                                onClick={handleContinueShopping}
                            />
                        </div>
                    )}
                </div>
            </div>
            {/* Modal de autenticación cuando el usuario no ha iniciado sesión */}
            <AuthModal 
                visible={authModalVisible} 
                onHide={() => setAuthModalVisible(false)} 
                onAuthSuccess={handleAuthSuccess} 
            />
        </>
    );
}


// Render modal outside return to avoid overlay issues
export function SlideoverCartAuthModalWrapper(props) {
    return (
        <>
            <AuthModal 
                visible={props.authModalVisible} 
                onHide={props.onHide} 
                onAuthSuccess={props.onAuthSuccess} 
            />
        </>
    );
}